import type { DashboardCampaign } from "@adpilot/shared";
import { IconLayoutGrid, IconX } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CampaignColumnCustomizer } from "@/components/campaign/CampaignColumnCustomizer";
import { SearchInput } from "@/components/patterns/SearchInput";
import { Button } from "@/components/ui/button";
import type { useCampaignTableColumns } from "@/hooks/use-campaign-table-columns";
import { cn } from "@/lib/utils";
import { CampaignFilter } from "./CampaignFilter";
import { policyLabel } from "./campaign-list-filters";
import type { CampaignList } from "./use-campaign-list";

type CampaignDelivery = "all" | "enabled" | "paused";
type CampaignColumnPreferences = ReturnType<typeof useCampaignTableColumns>;

export function CampaignListToolbar({
  list,
  campaigns,
  createMenu,
  columnPreferences,
  onDeliveryChange,
}: {
  list: CampaignList;
  campaigns: DashboardCampaign[];
  /** Present only for roles that can create campaigns. */
  createMenu: ReactNode;
  columnPreferences: CampaignColumnPreferences;
  onDeliveryChange: (delivery: CampaignDelivery) => void;
}) {
  const { t } = useTranslation("campaign");
  return (
    <div className="flex h-[50px] items-center justify-between gap-4 border-b border-line px-3">
      <div className="flex items-center gap-2">
        {createMenu ? (
          <>
            {createMenu}
            <Button size="sm" variant="outline" render={<Link to="/managed-groups" />}>
              <IconLayoutGrid data-icon="inline-start" />
              {t("list.manageGroups")}
            </Button>
          </>
        ) : null}
        <div className="flex h-8 items-center rounded-[8px] bg-subtle p-0.5">
          {(["all", "enabled", "paused"] as const).map((value) => (
            <button
              key={value}
              type="button"
              className={cn(
                "h-7 rounded-[6px] px-3 text-[11px] font-medium text-muted-foreground",
                list.delivery === value && "bg-surface text-ink",
              )}
              onClick={() => onDeliveryChange(value)}
            >
              {value === "all"
                ? t("list.tabs.all", { count: campaigns.length })
                : value === "enabled"
                  ? t("list.tabs.running", {
                      count: campaigns.filter((campaign) => campaign.status === "enabled").length,
                    })
                  : t("list.tabs.paused", {
                      count: campaigns.filter((campaign) => campaign.status === "paused").length,
                    })}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {list.policyFilters.map((value) => (
          <button
            key={value}
            type="button"
            className="flex h-8 items-center gap-1.5 rounded-[8px] border border-transparent bg-control-highlight px-2.5 text-[11px] text-ink transition-colors hover:bg-muted"
            onClick={() => list.removePolicyFilter(value)}
          >
            <span>{t("list.filters.policyChip", { label: policyLabel(t, value) })}</span>
            <IconX className="size-3" />
          </button>
        ))}
        <CampaignFilter
          policyFilters={list.policyFilters}
          runFilters={list.runFilters}
          managedGroupFilters={list.managedGroupFilters}
          managedGroupOptions={list.managedGroupOptions}
          onPolicyChange={list.changePolicyFilters}
          onRunChange={list.changeRunFilters}
          onManagedGroupChange={list.changeManagedGroupFilters}
        />
        <SearchInput
          value={list.search}
          onChange={(event) => list.changeSearch(event.target.value)}
          placeholder={t("list.searchPlaceholder")}
          showIcon
          textSize="compact"
          className="w-[220px]"
        />
        <CampaignColumnCustomizer
          visibleNonFixedColumns={columnPreferences.visibleNonFixedColumns}
          onApply={columnPreferences.setVisibleNonFixedColumns}
        />
      </div>
    </div>
  );
}
